import { useState, useEffect } from 'react';
import { Shield, CheckCircle, XCircle, AlertTriangle, Zap } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { scannerApi, type ScanResult } from '@/services/scannerApi';
import { githubApi } from '@/services/githubApi';
import { cn } from '@/lib/utils';

export default function ScannerPage() {
  const [repos, setRepos] = useState<string[]>([]);
  const [scans, setScans] = useState<Record<string, ScanResult>>({});
  const [scanning, setScanning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    githubApi.listRepos().then((list) => {
      setRepos(list.filter((r) => r.connected).map((r) => r.full_name));
    }).finally(() => setLoading(false));
  }, []);

  async function handleScan(repo: string) {
    setScanning(repo);
    try {
      const result = await scannerApi.scanRepo(repo);
      setScans((prev) => ({ ...prev, [repo]: result }));
    } finally {
      setScanning(null);
    }
  }

  async function handleScanAll() {
    for (const repo of repos) {
      await handleScan(repo);
    }
  }

  if (loading) {
    return <LoadingState size="lg" />;
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Project Health Scanner</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Verify repos follow org standards.</p>
        </div>
        <button onClick={handleScanAll} disabled={!!scanning} className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
          <Shield className="w-4 h-4" /> Scan All
        </button>
      </div>

      <div className="space-y-4">
        {repos.map((repo) => {
          const scan = scans[repo];
          return (
            <div key={repo} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  {scan ? (
                    <div className={cn('text-2xl font-bold', scan.score >= 80 ? 'text-emerald-600' : scan.score >= 50 ? 'text-amber-600' : 'text-red-600')}>
                      {scan.score}%
                    </div>
                  ) : (
                    <div className="text-2xl font-bold text-slate-300">—</div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{repo}</p>
                    {scan && <p className="text-xs text-slate-500">{scan.passed} passed, {scan.failed} failed</p>}
                  </div>
                </div>
                <button onClick={() => handleScan(repo)} disabled={scanning === repo} className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50">
                  {scanning === repo ? <Spinner size="sm" tone="current" /> : <Zap className="w-3.5 h-3.5" />}
                  {scanning === repo ? 'Scanning...' : 'Scan'}
                </button>
              </div>
              {scan && (
                <div className="border-t border-slate-200 dark:border-slate-800 px-4 py-2 space-y-1">
                  {scan.results.map((r) => (
                    <div key={r.ruleId} className="flex items-center gap-2 py-1">
                      {r.passed ? (
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      ) : r.severity === 'error' ? (
                        <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      )}
                      <span className={cn('text-xs flex-1', r.passed ? 'text-slate-500' : 'text-slate-700 dark:text-slate-300')}>
                        {r.ruleName}
                      </span>
                      {!r.passed && r.autoFixable && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">Auto-fixable</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {repos.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-12">No connected repositories. Connect repos in Settings first.</p>
        )}
      </div>
    </div>
  );
}
